import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Check, Sparkles, TrendingUp, Zap, Rocket, AlertCircle, Crown,
  ShieldCheck, Lock, ArrowRight, HelpCircle, CheckCircle2, Shield
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ICONS = { free: Sparkles, trial: Zap, pro: TrendingUp, scale: Rocket };
const ACCENTS = { free: "#64748B", trial: "#2563EB", pro: "#2563EB", scale: "#7C3AED" };

const DEFAULT_PLANS = [
  {
    id: "free",
    name: "Free Trial",
    price_monthly: 0,
    features: [
      "7-Day Full Access Trial",
      "500 contacts",
      "1,000 emails / month",
      "20 AI generations / month",
      "3 automations",
      "Standard support"
    ],
    limits: {
      contacts: 500,
      emails_per_month: 1000,
      ai_generations_per_month: 20,
      social_posts_per_month: 20,
      automations: 3,
      team_members: 1
    }
  },
  {
    id: "pro",
    name: "Pro",
    price_monthly: 49,
    features: [
      "Unlimited contacts",
      "50,000 emails / month",
      "500 AI generations / month",
      "Unlimited automations",
      "Social media integration",
      "Priority 24/7 support"
    ],
    limits: {
      contacts: -1,
      emails_per_month: 50000,
      ai_generations_per_month: 500,
      social_posts_per_month: 500,
      automations: -1,
      team_members: 5
    }
  },
  {
    id: "scale",
    name: "Scale",
    price_monthly: 99,
    features: [
      "Unlimited contacts & locations",
      "Unlimited emails & campaigns",
      "Unlimited AI generations",
      "Dedicated account manager",
      "Custom domain & whitelabel",
      "Full API & webhooks access"
    ],
    limits: {
      contacts: -1,
      emails_per_month: -1,
      ai_generations_per_month: -1,
      social_posts_per_month: -1,
      automations: -1,
      team_members: -1
    }
  }
];

export default function Billing() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [current, setCurrent] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [checkoutFor, setCheckoutFor] = useState(null);
  const [startingTrial, setStartingTrial] = useState(false);

  const isMasterUser =
    user?.role === "master" ||
    user?.role === "admin" ||
    current?.is_master ||
    ["colibrisocialclub@gmail.com", "contact@amanbiswalthakur.in", "thyzfoto@gmail.com", "admin@weddingsthyzfoto.com"].includes(user?.email);

  const isTrialActive = Boolean(
    (current?.subscription?.is_trial || user?.is_trial) &&
    (current?.subscription?.trial_days_left === undefined || current?.subscription?.trial_days_left > 0)
  );

  const hasPaidSubscription = Boolean(
    ["pro", "scale", "agency"].includes((current?.subscription?.plan_id || user?.plan || "").toLowerCase()) &&
    ["active", "trialing", "cancelling"].includes((current?.subscription?.status || "active").toLowerCase())
  );

  const requiresSubscription = !isMasterUser && !isTrialActive && !hasPaidSubscription;

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      await api.post("/billing/start-trial");
      toast.success("7-Day Free Trial activated! Workspace unlocked.");
      if (refresh) await refresh();
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not activate trial");
    } finally {
      setStartingTrial(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.allSettled([
        api.get("/billing/plans"),
        api.get("/billing/subscription")
      ]);
      if (p.status === "fulfilled" && p.value?.data?.plans) {
        setPlans(p.value.data.plans);
      }
      if (s.status === "fulfilled" && s.value?.data) {
        setCurrent(s.value.data);
      } else {
        setCurrent({
          subscription: {
            plan_id: user?.plan || (user?.is_trial ? "trial" : "free"),
            status: user?.is_trial ? "active" : "inactive",
            is_trial: Boolean(user?.is_trial),
            trial_days_left: 7
          },
          plan: {
            id: user?.plan || "free",
            name: user?.is_trial ? "7-Day Free Trial" : "Starter Tier",
            limits: { contacts: 500, emails_per_month: 1000, ai_generations_per_month: 20 }
          },
          usage: { contacts: 0, emails_sent: 0, ai_generations: 0 }
        });
      }
    } catch (e) {
      console.error("Billing load error:", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const upgrade = async (plan_id) => {
    if (plan_id === "free") return;
    setCheckoutFor(plan_id);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id,
        billing_cycle: cycle,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setCheckoutFor(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel subscription at the end of this billing period?")) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription will cancel at period end");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const currentPlanId = current?.subscription?.plan_id || "free";
  const isCancelling = current?.subscription?.status === "cancelling";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-10" data-testid="billing-page">
      {/* ── Header ── */}
      <div className="text-center max-w-2xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Simple, Transparent Pricing</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
          Flexible Plans for Growing Agencies
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Scale your marketing, automate your pipelines, and manage unlimited client accounts without surprise fees.
        </p>
      </div>

      {/* ── Status Banners ── */}
      {/* 1. Subscription Plan / Trial Required Banner */}
      {requiresSubscription && !isMasterUser && (
        <div
          data-testid="subscription-required-banner"
          className="p-6 sm:p-7 rounded-3xl border border-blue-200/80 dark:border-blue-900/60 bg-blue-50/60 dark:bg-blue-950/20 shadow-xs space-y-4"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-600/10 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-2xl border border-blue-500/20 shrink-0">
                <Lock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <span>Select a Plan to Unlock Your Workspace</span>
                  <Badge variant="outline" className="text-blue-600 border-blue-300 dark:border-blue-800 text-[11px] font-semibold">
                    Trial Concluded
                  </Badge>
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed">
                  Your complimentary 7-day trial period has ended. Choose a subscription tier below to immediately restore full access to campaigns, contacts, automations, and AI tools.
                </p>
              </div>
            </div>
            {!user?.trial_used && (
              <Button
                data-testid="activate-trial-btn"
                onClick={handleStartTrial}
                disabled={startingTrial}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs h-10 px-5 rounded-xl shadow-md gap-2 cursor-pointer shrink-0"
              >
                <Zap className="w-4 h-4 fill-white" />
                <span>{startingTrial ? "Activating Trial…" : "Start 7-Day Free Trial"}</span>
              </Button>
            )}
          </div>
        </div>
      )}

      {/* 2. Master Panel VIP Notice */}
      {isMasterUser && (
        <div className="p-6 rounded-2xl border border-amber-400/40 bg-gradient-to-r from-amber-500/10 via-blue-500/5 to-purple-500/10 flex items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold text-2xl border border-amber-500/30">
              👑
            </div>
            <div>
              <div className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2">
                <span>Master VIP Permanent Administrator License Active</span>
                <Badge className="bg-emerald-600 text-white text-[10px]">Lifetime Unlimited Access</Badge>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 max-w-2xl">
                Protected Master Account: all features, sub-accounts, high-volume campaigns, and white-label tools are unlocked and fully operational.
              </p>
            </div>
          </div>
          <Badge variant="outline" className="text-xs text-amber-600 dark:text-amber-400 border-amber-500/40 px-3 py-1 font-bold shrink-0 hidden sm:inline-flex">
            Master Tier
          </Badge>
        </div>
      )}

      {/* 3. 7-Day Free Trial Active Banner */}
      {!isMasterUser && (current?.subscription?.is_trial || user?.is_trial) && (
        <div className="p-6 rounded-2xl border border-blue-500/40 bg-gradient-to-r from-blue-600/10 via-indigo-500/5 to-purple-600/10 flex items-center justify-between gap-4 shadow-sm" data-testid="trial-banner">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-500/20 text-blue-600 flex items-center justify-center font-bold text-2xl border border-blue-500/30">
              🎁
            </div>
            <div>
              <div className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2">
                <span>7-Day Free Trial Active</span>
                <Badge className="bg-blue-600 text-white text-[10px]">
                  {current?.subscription?.trial_days_left ?? 7} Days Left
                </Badge>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 max-w-2xl">
                Enjoy full access to all Pro features. Choose a subscription anytime before your 7 days end to prevent workspace interruption.
              </p>
            </div>
          </div>
          <Badge variant="outline" className="text-xs text-blue-600 border-blue-500/40 px-3 py-1 font-bold shrink-0 hidden sm:inline-flex">
            Pro Features Unlocked
          </Badge>
        </div>
      )}

      {/* ── Modern Billing Cycle Switcher ── */}
      <div className="flex items-center justify-center pt-2">
        <div className="inline-flex items-center p-1.5 rounded-full bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 shadow-inner">
          <button
            data-testid="cycle-monthly"
            onClick={() => setCycle("monthly")}
            className={`px-6 py-2 text-xs font-bold rounded-full transition-all duration-200 ${cycle === "monthly"
                ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              }`}
          >
            Monthly Billing
          </button>
          <button
            data-testid="cycle-yearly"
            onClick={() => setCycle("yearly")}
            className={`px-6 py-2 text-xs font-bold rounded-full transition-all duration-200 flex items-center gap-2 ${cycle === "yearly"
                ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              }`}
          >
            <span>Annual Billing</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-500 text-white shadow-xs">
              Save 20%
            </span>
          </button>
        </div>
      </div>

      {/* ── Modern SaaS Pricing Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
        {plans.map((p) => {
          const Icon = ICONS[p.id] || Zap;
          const isCurrent = currentPlanId === p.id;
          const isFree = p.id === "free";
          const isPopular = p.id === "pro";
          const isScale = p.id === "scale";

          const monthlyPrice = p.price_monthly;
          const calculatedPrice = isMasterUser
            ? 0
            : cycle === "yearly"
              ? Math.round(monthlyPrice * 0.8)
              : monthlyPrice;
          const suffix = cycle === "yearly" ? "/mo (billed annually)" : "/mo";

          return (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`relative rounded-3xl p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 ${isPopular
                  ? "bg-white dark:bg-slate-900 border-2 border-blue-600 shadow-xl shadow-blue-500/10 md:-translate-y-2"
                  : isScale
                    ? "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-lg"
                    : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md"
                }`}
            >
              {/* Popular Badge */}
              {isPopular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[11px] font-extrabold bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md tracking-wider uppercase">
                  ⭐ Most Popular
                </div>
              )}

              {isScale && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold bg-slate-900 text-white dark:bg-purple-900 shadow-xs uppercase tracking-wider">
                  Full Agency Suite
                </div>
              )}

              <div>
                {/* Plan Header */}
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-2xl flex items-center justify-center"
                      style={{ background: `${ACCENTS[p.id] || "#2563EB"}15` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: ACCENTS[p.id] || "#2563EB" }} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white">{p.name}</h3>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        {p.id === "free"
                          ? "Basic exploration"
                          : p.id === "pro"
                            ? "Best for solo founders & studios"
                            : "For agencies & multi-locations"}
                      </p>
                    </div>
                  </div>

                  {(isCurrent || (isMasterUser && p.id === "scale")) && (
                    <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 text-[10px] font-bold">
                      {isMasterUser ? "Master VIP" : "Active"}
                    </Badge>
                  )}
                </div>

                {/* Price Display */}
                <div className="my-6 pb-6 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 dark:text-white">
                      ${calculatedPrice}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                      {isMasterUser ? "VIP (Free)" : isFree ? "forever" : suffix}
                    </span>
                  </div>
                  {cycle === "yearly" && !isFree && !isMasterUser && (
                    <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold mt-1">
                      Billed as ${calculatedPrice * 12}/year (save ${(monthlyPrice - calculatedPrice) * 12})
                    </div>
                  )}
                </div>

                {/* Features List */}
                <div className="space-y-3 mb-8">
                  <div className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                    Included Features:
                  </div>
                  <ul className="space-y-2.5">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-xs text-slate-600 dark:text-slate-300">
                        <div className="w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="w-2.5 h-2.5 stroke-[3]" />
                        </div>
                        <span className="leading-tight">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Action Button */}
              <div>
                <Button
                  data-testid={`upgrade-${p.id}`}
                  disabled={(!isMasterUser && (isCurrent || isFree)) || checkoutFor === p.id}
                  onClick={() => {
                    if (isMasterUser) {
                      toast.success(`Master VIP Privilege: ${p.name} tier is permanently unlocked.`);
                      return;
                    }
                    upgrade(p.id);
                  }}
                  className={`w-full font-bold text-xs h-11 rounded-xl shadow-xs transition-all ${isMasterUser
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                      : isPopular
                        ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-500/20"
                        : isCurrent || isFree
                          ? "bg-slate-100 dark:bg-slate-800 text-slate-400 border border-slate-200 dark:border-slate-700"
                          : "bg-slate-900 hover:bg-black text-white dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900"
                    }`}
                >
                  {isMasterUser
                    ? "Master VIP Unlocked (Free)"
                    : isCurrent
                      ? "Current Active Plan"
                      : isFree
                        ? "Free Tier"
                        : checkoutFor === p.id
                          ? "Opening Stripe Checkout…"
                          : `Upgrade to ${p.name}`}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Current Usage Meter ── */}
      {current && current.plan && (
        <Card className="p-6 sm:p-8 rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-slate-100 dark:border-slate-800">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Current Resource Allocation
                </span>
                <Badge className="capitalize bg-blue-600 text-white text-[10px]">
                  {current.plan?.name || "Standard"} Tier
                </Badge>
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-1">
                Usage &amp; Workspace Quota
              </h3>
            </div>
            {currentPlanId !== "free" && !isCancelling && !isMasterUser && (
              <Button
                data-testid="cancel-subscription"
                variant="outline"
                size="sm"
                className="text-xs text-slate-500 hover:text-red-600 border-slate-200 dark:border-slate-700"
                onClick={cancel}
              >
                Cancel subscription
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <UsageBar label="Saved Contacts" used={current?.usage?.contacts || 0} limit={current?.plan?.limits?.contacts ?? 500} />
            <UsageBar label="Monthly Outbound Emails" used={current?.usage?.emails_sent || 0} limit={current?.plan?.limits?.emails_per_month ?? 1000} />
            <UsageBar label="AI Copilot Generations" used={current?.usage?.ai_generations || 0} limit={current?.plan?.limits?.ai_generations_per_month ?? 20} />
          </div>
        </Card>
      )}

      {/* ── Trust & Security Badges ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 text-center">
        <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-800 flex items-center justify-center gap-3">
          <ShieldCheck className="w-5 h-5 text-blue-600 shrink-0" />
          <div className="text-left">
            <div className="text-xs font-bold text-slate-900 dark:text-white">Bank-Grade Security</div>
            <div className="text-[11px] text-slate-500">256-bit encrypted via Stripe</div>
          </div>
        </div>
        <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-800 flex items-center justify-center gap-3">
          <Zap className="w-5 h-5 text-emerald-600 shrink-0" />
          <div className="text-left">
            <div className="text-xs font-bold text-slate-900 dark:text-white">Instant Provisioning</div>
            <div className="text-[11px] text-slate-500">Features unlock in real time</div>
          </div>
        </div>
        <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-800 flex items-center justify-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-purple-600 shrink-0" />
          <div className="text-left">
            <div className="text-xs font-bold text-slate-900 dark:text-white">Cancel Anytime</div>
            <div className="text-[11px] text-slate-500">No long-term contracts</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function UsageBar({ label, used = 0, limit = 500 }) {
  const safeUsed = Number(used) || 0;
  const safeLimit = limit === undefined || limit === null ? 500 : limit;
  const unlimited = safeLimit === -1;
  const pct = unlimited ? 0 : Math.min(100, Math.round((safeUsed / Math.max(1, safeLimit)) * 100));
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-xs">
        <span className="font-semibold text-slate-600 dark:text-slate-400">{label}</span>
        <span className="font-bold text-slate-900 dark:text-white">
          {safeUsed.toLocaleString()} / {unlimited ? "Unlimited (∞)" : safeLimit.toLocaleString()}
        </span>
      </div>
      <Progress value={pct} className="h-2 rounded-full" />
    </div>
  );
}
